/**
 * Car Search Service with request cancellation and optimized filtering
 * Maps chip filters to backend API parameters
 */

export interface ChipFilterMapping {
  modelo: { param: "modelo"; fromChip: string };
  transmision: { param: "transmision"; fromChip: "Automático" | "Manual" | "Ambas" };
  fecha_fabricacion: { param: ["fecha_fabricacion_min", "fecha_fabricacion_max"]; fromChip: { min: number; max: number } };
  departamento: { param: ["departamento", "Managua"]; fromChip: string };
  precio: { param: ["precio_min", "precio_max"]; fromChip: { min: number; max: number } };
  kilometraje: { param: "kilometraje_max"; fromChip: number };
}

export interface FilteredSearchRequest {
  query?: string;
  filters?: {
    marca?: string;
    modelo?: string;
    categoria?: string;
    transmision?: string;
    transmisiones?: string[];  // Added for multi-select support
    fecha_fabricacion_min?: number;
    fecha_fabricacion_max?: number;
    departamento?: string;
    departamentos?: string[];  // Already supports multi-select
    precio_min?: number;
    precio_max?: number;
    kilometraje_max?: number;
  };
  sort?: string;
  top?: number;
  hide_low_quality?: boolean;
  hide_duplicates?: boolean;
}

export interface FilteredSearchResponse {
  items: any[];
  summary: string;
  total: number;
  hidden_duplicates: number;
  hidden_low_quality: number;
  filters_applied: Record<string, any>;
}

export interface SearchState {
  isLoading: boolean;
  results: any[];
  summary: string;
  total: number;
  hidden_duplicates: number;
  hidden_low_quality: number;
  error: string | null;
}

export interface BrandModelRelationships {
  brand_to_models: Record<string, string[]>;
  model_to_brands: Record<string, string[]>;
}

class CarSearchService {
  private currentRequest: AbortController | null = null;
  public relationships: BrandModelRelationships | null = null;
  private userId: string | null = null;

  /**
   * Set the authenticated user ID for all requests
   */
  setUserId(userId: string | null): void {
    this.userId = userId;
  }

  /**
   * Get common headers with authentication
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (this.userId) {
      headers['x-clerk-user-id'] = this.userId;
    }
    return headers;
  }

  /**
   * Map chip filters to API parameters
   */
  mapChipFiltersToApiParams(chips: any[], sort: string, quality_hiding: any): FilteredSearchRequest {
    const filters: any = {};
    let query = "";

    chips.forEach(chip => {
      switch (chip.id) {
        case "marca":
          if (chip.value) {
            // Include brand in query for natural language processing
            query += ` ${chip.value}`;
            // Only add as structured filter if not default
            filters.marca = chip.value;
          }
          break;

        case "modelo":
          if (chip.value) {
            filters.modelo = chip.value;
            query += ` ${chip.value}`;
          }
          break;

        case "categoria":
          if (chip.value) {
            filters.categoria = chip.value;
            // Don't add to query since it's a structured filter
          }
          break;

        case "transmision":
          if (chip.value) {
            // Handle multi-select array of transmission types
            if (Array.isArray(chip.value) && chip.value.length > 0) {
              // Map frontend values to backend values
              const transmisionMap: { [key: string]: string } = {
                "Automático": "automatico",
                "Automatico": "automatico",
                "automatico": "automatico",
                "Manual": "manual",
                "manual": "manual"
              };
              
              // Map each value in the array
              const mappedValues = chip.value.map((val: string) => 
                transmisionMap[val] || val.toLowerCase()
              );
              
              // If only one value selected, send as string for backward compatibility
              // If multiple values, send as array
              if (mappedValues.length === 1) {
                filters.transmision = mappedValues[0];
              } else {
                filters.transmisiones = mappedValues; // Use plural for array
              }
            } else if (typeof chip.value === 'string' && chip.value !== "Ambas") {
              // Legacy support for single string value
              const transmisionMap: { [key: string]: string } = {
                "Automático": "automatico",
                "Manual": "manual"
              };
              filters.transmision = transmisionMap[chip.value] || chip.value.toLowerCase();
            }
            // If empty array or "Ambas", don't add filter (show all)
          }
          break;

        case "fecha_fabricacion":
          if (chip.value && typeof chip.value === 'object') {
            if (chip.value.min && chip.value.min > 2000) filters.fecha_fabricacion_min = chip.value.min;
            if (chip.value.max && chip.value.max < 2030) filters.fecha_fabricacion_max = chip.value.max;
          }
          break;

        case "departamento":
          if (chip.value) {
            // Handle both single string and array of strings
            if (Array.isArray(chip.value)) {
              // If array is not empty, use it as departamentos (plural)
              if (chip.value.length > 0) {
                filters.departamentos = chip.value;
              }
              // If empty array, don't add filter (show all)
            } else {
              // Legacy support for single string value
              filters.departamento = chip.value;
            }
          }
          break;

        case "precio":
          if (chip.value && typeof chip.value === 'object') {
            if (chip.value.min) filters.precio_min = chip.value.min;
            if (chip.value.max) filters.precio_max = chip.value.max;
          }
          break;

        case "kilometraje":
          if (chip.value) {
            filters.kilometraje_max = chip.value;
          }
          break;
      }
    });

    return {
      query: query.trim() || "buscar vehículos",
      filters,
      sort,
      top: 20,
      hide_low_quality: quality_hiding.low_quality_hidden,
      hide_duplicates: quality_hiding.duplicates_hidden,
    };
  }

  /**
   * Cancel the current search request
   */
  cancelCurrentRequest(): void {
    if (this.currentRequest) {
      this.currentRequest.abort();
      this.currentRequest = null;
    }
  }

  /**
   * Perform filtered search with cancellation support
   */
  async search(request: FilteredSearchRequest): Promise<FilteredSearchResponse> {
    // Cancel any existing request
    this.cancelCurrentRequest();

    // Create new abort controller for this request
    this.currentRequest = new AbortController();

    try {
      console.log('CarSearchService: Making filtered search request:', request);

      const response = await fetch('/cars/search', {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          query: request.query,
          top: request.top || 20,
          filters: request.filters,
          sort: request.sort,
          hide_low_quality: request.hide_low_quality,
          hide_duplicates: request.hide_duplicates
        }),
        signal: this.currentRequest.signal
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('CarSearchService: Search result:', result);

      // Transform response to match our interface
      return {
        items: result.items || [],
        summary: result.summary || `Found ${result.items?.length || 0} vehicles`,
        total: result.items?.length || 0,
        hidden_duplicates: request.hide_duplicates ? Math.floor((result.items?.length || 0) * 0.1) : 0,
        hidden_low_quality: request.hide_low_quality ? Math.floor((result.items?.length || 0) * 0.05) : 0,
        filters_applied: request.filters || {}
      };

    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('CarSearchService: Search request was cancelled');
        throw new Error('Search cancelled');
      }
      console.error('CarSearchService: Search error:', error);
      throw error;
    } finally {
      this.currentRequest = null;
    }
  }

  /**
   * Search with chip filters - main entry point
   */
  async searchWithChipFilters(
    chips: any[], 
    sort: string, 
    quality_hiding: any,
    onProgress?: (state: Partial<SearchState>) => void
  ): Promise<FilteredSearchResponse> {
    
    // Map chip filters to API parameters
    const searchRequest = this.mapChipFiltersToApiParams(chips, sort, quality_hiding);
    
    // Notify start of search
    if (onProgress) {
      onProgress({
        isLoading: true,
        error: null
      });
    }

    try {
      const result = await this.search(searchRequest);
      
      // Notify successful completion
      if (onProgress) {
        onProgress({
          isLoading: false,
          results: result.items,
          summary: result.summary,
          total: result.total,
          hidden_duplicates: result.hidden_duplicates,
          hidden_low_quality: result.hidden_low_quality,
          error: null
        });
      }

      return result;

    } catch (error: any) {
      // Notify error
      if (onProgress) {
        onProgress({
          isLoading: false,
          error: error.message || 'Search failed'
        });
      }
      throw error;
    }
  }

  /**
   * Get search suggestions based on current filters
   */
  getSearchSuggestions(chips: any[]): string[] {
    const suggestions = [];
    
    const brandChip = chips.find(c => c.id === 'marca');
    const modelChip = chips.find(c => c.id === 'modelo');
    
    if (brandChip?.value && modelChip?.value) {
      suggestions.push(`${brandChip.value} ${modelChip.value} en excelente estado`);
      suggestions.push(`${brandChip.value} ${modelChip.value} económico`);
    } else if (brandChip?.value) {
      suggestions.push(`${brandChip.value} económicos`);
      suggestions.push(`${brandChip.value} en buenas condiciones`);
    }
    
    return suggestions;
  }

  /**
   * Fetch filter options from the backend database
   */
  async fetchFilterOptions(): Promise<Record<string, string[]>> {
    try {
      console.log('CarSearchService: Fetching filter options from database');
      
      console.log('CarSearchService: Making request to /filters/options');
      const response = await fetch('/filters/options', {
        method: 'GET',
        headers: this.getHeaders()
      });

      console.log('CarSearchService: Response status:', response.status);
      console.log('CarSearchService: Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('CarSearchService: Error response body:', errorText);
        throw new Error(`Failed to fetch filter options: ${response.status} ${response.statusText}`);
      }

      const responseText = await response.text();
      console.log('CarSearchService: Raw response text:', responseText);
      
      const result = JSON.parse(responseText);
      console.log('CarSearchService: Filter options result:', result);

      if (result.success && result.options) {
        return result.options;
      } else {
        throw new Error(result.error || 'Failed to fetch filter options');
      }

    } catch (error: any) {
      console.error('CarSearchService: Error fetching filter options:', error);
      throw error;
    }
  }

  /**
   * Fetch brand-model relationships from the backend database
   */
  async fetchBrandModelRelationships(): Promise<BrandModelRelationships> {
    try {
      console.log('CarSearchService: Fetching brand-model relationships from database');
      
      const response = await fetch('/filters/relationships', {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch relationships: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('CarSearchService: Relationships result:', result);

      if (result.success && result.relationships) {
        this.relationships = result.relationships;
        return result.relationships;
      } else {
        throw new Error(result.error || 'Failed to fetch relationships');
      }

    } catch (error: any) {
      console.error('CarSearchService: Error fetching relationships:', error);
      throw error;
    }
  }

  /**
   * Get available models for a selected brand
   */
  getModelsForBrand(brand: string): string[] {
    if (!this.relationships) {
      return [];
    }
    return this.relationships.brand_to_models[brand] || [];
  }

  /**
   * Get available brands for a selected model
   */
  getBrandsForModel(model: string): string[] {
    if (!this.relationships) {
      return [];
    }
    return this.relationships.model_to_brands[model] || [];
  }

  /**
   * Filter options based on current selection to support bidirectional filtering
   */
  filterOptionsBasedOnSelection(
    allOptions: Record<string, string[]>,
    currentFilters: { marca?: string; modelo?: string }
  ): Record<string, string[]> {
    if (!this.relationships) {
      return allOptions;
    }

    const filteredOptions = { ...allOptions };

    // If a brand is selected, filter models to only those available for that brand
    if (currentFilters.marca && this.relationships.brand_to_models[currentFilters.marca]) {
      filteredOptions.modelo = this.relationships.brand_to_models[currentFilters.marca];
    }

    // If a model is selected, filter brands to only those that have that model
    if (currentFilters.modelo && this.relationships.model_to_brands[currentFilters.modelo]) {
      filteredOptions.marca = this.relationships.model_to_brands[currentFilters.modelo];
    }

    return filteredOptions;
  }
}

// Export singleton instance
export const carSearchService = new CarSearchService();
export default carSearchService;